import { useEffect, useRef } from "react";
import { toast } from "sonner";

interface GoogleDrivePickerProps {
  onFileSelect: (file: File) => void;
  onClose: () => void;
  isOpen: boolean;
}

declare global {
  interface Window {
    google: any;
    gapi: any;
  }
}

const API_LOAD_TIMEOUT_MS = 15000;

/** MIME types the Drive picker should show — aligned with backend + local upload. */
const PICKER_MIME_TYPES = [
  "audio/mpeg",
  "audio/mp3",
  "audio/wav",
  "audio/x-wav",
  "audio/wave",
  "audio/mp4",
  "audio/x-m4a",
  "audio/aac",
  "audio/flac",
  "audio/ogg",
  "audio/opus",
  "audio/webm",
  "video/mp4",
  "video/webm",
].join(",");

const EXT_TO_MIME: Record<string, string> = {
  webm: "audio/webm",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  m4a: "audio/mp4",
  mp4: "video/mp4",
  ogg: "audio/ogg",
  oga: "audio/ogg",
  flac: "audio/flac",
  aac: "audio/aac",
  opus: "audio/ogg",
};

const fileExtension = (name: string): string =>
  name.split(".").pop()?.toLowerCase() ?? "";

const isSupportedAudioFile = (mime: string, name: string): boolean => {
  const type = (mime || "").split(";")[0].trim().toLowerCase();
  if (type.startsWith("audio/")) return true;
  if (type === "video/mp4" || type === "video/webm") return true;
  return fileExtension(name) in EXT_TO_MIME;
};

const resolveMimeType = (mime: string, name: string): string => {
  const type = (mime || "").split(";")[0].trim().toLowerCase();
  if (type && type !== "application/octet-stream") {
    if (type.startsWith("audio/") || type === "video/mp4" || type === "video/webm") {
      return type;
    }
  }
  return EXT_TO_MIME[fileExtension(name)] || type || "audio/mpeg";
};

const hasGoogleIdentityServices = (): boolean =>
  Boolean(window.google?.accounts?.oauth2);

const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });

const GoogleDrivePicker: React.FC<GoogleDrivePickerProps> = ({ onFileSelect, onClose, isOpen }) => {
  const pickerApiLoaded = useRef(false);
  const oauthToken = useRef<string | null>(null);
  const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearLoadTimeout = () => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  };

  // Timeout only covers API/auth load — not the user picking a file.
  useEffect(() => {
    if (!isOpen) {
      clearLoadTimeout();
      return;
    }

    loadTimeoutRef.current = setTimeout(() => {
      console.log("🔧 [GoogleDrivePicker] Auto-closing after API load timeout");
      toast.error("שגיאה בטעינת Google Drive - נסה שוב");
      onClose();
    }, API_LOAD_TIMEOUT_MS);

    return () => clearLoadTimeout();
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const loadGoogleAPIs = async () => {
      try {
        if (!hasGoogleIdentityServices()) {
          await loadScript("https://accounts.google.com/gsi/client");
        }

        if (!window.gapi) {
          await loadScript("https://apis.google.com/js/api.js");
        }

        await new Promise((resolve) => {
          window.gapi.load("picker", () => {
            resolve(undefined);
          });
        });

        pickerApiLoaded.current = true;
        authenticate();
      } catch (error) {
        console.error("❌ [GoogleDrivePicker] Error loading Google APIs:", error);
        toast.error("שגיאה בטעינת Google Drive");
        onClose();
      }
    };

    loadGoogleAPIs();
  }, [isOpen]);

  const createPicker = () => {
    if (!pickerApiLoaded.current || !window.google?.picker || !window.gapi) {
      console.error("❌ [GoogleDrivePicker] Google APIs not loaded", {
        pickerApiLoaded: pickerApiLoaded.current,
        google: !!window.google,
        gapi: !!window.gapi,
      });
      return;
    }

    const apiKey = import.meta.env.VITE_GOOGLE_API_KEY;

    if (!apiKey) {
      console.error("❌ [GoogleDrivePicker] VITE_GOOGLE_API_KEY is not set!");
      toast.error("Google API Key לא מוגדר");
      onClose();
      return;
    }

    try {
      const docsView = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS);
      docsView.setMimeTypes(PICKER_MIME_TYPES);
      docsView.setIncludeFolders(true);
      docsView.setSelectFolderEnabled(false);

      const picker = new window.google.picker.PickerBuilder()
        .addView(docsView)
        .setOAuthToken(oauthToken.current || "")
        .setDeveloperKey(apiKey)
        .setCallback(pickerCallback)
        .setOrigin(window.location.protocol + "//" + window.location.host)
        .setSelectableMimeTypes(PICKER_MIME_TYPES)
        .build();

      try {
        picker.setVisible(true);
        clearLoadTimeout();
      } catch (error) {
        console.error("❌ [GoogleDrivePicker] Error setting picker visible:", error);
        toast.error("שגיאה בפתיחת Google Drive Picker");
        onClose();
      }
    } catch (error) {
      console.error("❌ [GoogleDrivePicker] Error creating picker:", error);
      toast.error("שגיאה בפתיחת Google Drive");
      onClose();
    }
  };

  const pickerCallback = (data: any) => {
    if (data.action === window.google.picker.Action.PICKED) {
      const file = data.docs[0];
      if (file) {
        downloadFileFromDrive(file);
      }
    } else if (data.action === window.google.picker.Action.CANCEL) {
      onClose();
    }
  };

  const downloadFileFromDrive = async (file: any) => {
    try {
      const downloadUrl = `https://www.googleapis.com/drive/v3/files/${file.id}?alt=media&supportsAllDrives=true`;
      const response = await fetch(downloadUrl, {
        headers: {
          Authorization: `Bearer ${oauthToken.current}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const mimeType = resolveMimeType(file.mimeType || blob.type, file.name || "");
      const audioFile = new File([blob], file.name, { type: mimeType });

      if (!isSupportedAudioFile(audioFile.type, audioFile.name)) {
        toast.error("קובץ לא נתמך. אנא בחר קובץ אודיו או MP4 בלבד");
        onClose();
        return;
      }

      onFileSelect(audioFile);
      onClose();
      toast.success("קובץ נבחר מ-Google Drive בהצלחה!");
    } catch (error) {
      console.error("❌ [GoogleDrivePicker] Error downloading file from Drive:", error);
      toast.error("שגיאה בהורדת הקובץ מ-Google Drive");
      onClose();
    }
  };

  const authenticate = async () => {
    try {
      if (!hasGoogleIdentityServices()) {
        throw new Error("Google Identity Services not available");
      }

      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || "",
        scope: "https://www.googleapis.com/auth/drive.readonly",
        callback: (response: any) => {
          oauthToken.current = response.access_token;
          createPicker();
        },
        error_callback: (error: any) => {
          console.error("❌ [GoogleDrivePicker] Token error:", error);
          toast.error("שגיאה בהתחברות ל-Google Drive");
          onClose();
        },
      });

      tokenClient.requestAccessToken();
    } catch (error) {
      console.error("❌ [GoogleDrivePicker] Authentication error:", error);
      toast.error("שגיאה בהתחברות ל-Google Drive");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/10 backdrop-blur-xs">
      <div className="flex items-center gap-3 rounded-lg bg-background p-6 shadow-lg border">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        <span className="text-sm font-medium">בחירת קובץ מגוגל דרייב</span>
      </div>
    </div>
  );
};

export default GoogleDrivePicker;
